// src/components/wallet/YinYangRewards.js
import React, { useEffect, useState, useCallback } from "react";
import { formatCurrencyExtended }   from "../../utils.js";
import { Button }                   from "primereact/button";
import { Dropdown }                 from "primereact/dropdown";
import { Skeleton }                 from "primereact/skeleton";
import { Progress }                 from "@nextui-org/react";
import { formatDistanceToNow }      from "date-fns";
import distributorAbi               from "../../contracts/dividendToken_1_distributorAbi.js";
import Web3                         from "web3";

/* ─── stake-lock presets (seconds) ─────────────────────────────── */
const stakingOptions = [
  { label: "2 Weeks", value: 1_314_000 },
  { label: "4 Weeks", value: 2_628_000 },
  { label: "60 Days", value: 5_256_000 }
];

/* ─── main component ───────────────────────────────────────────── */
const YinYangRewards = React.memo(
({ token, account, contracts, tokenSelections,
   viewTotalDistributed = false, enableClaiming = false }) =>
{
  /* ─── state ─────────────────────────────────────────── */
  const [isLoading,        setIsLoading]        = useState(true);
  const [contractData,     setContractData]     = useState(null);   // totals / holders / payouts
  const [unclaimedRewards, setUnclaimedRewards] = useState(0);
  const [claimedDividends, setClaimedDividends] = useState(0);
  const [isClaiming,       setIsClaiming]       = useState(false);
  const [isStaking,        setIsStaking]        = useState(false);

  const [stakingInfo,      setStakingInfo]      = useState({ until:0, bonus:0 });
  const [countdown,        setCountdown]        = useState("Not Staked");
  const [selectedDuration, setSelectedDuration] = useState(null);

  const buildContract = (addr, abi) => {
    const web3 = new Web3(window.ethereum);
    return new web3.eth.Contract(abi, addr);
  };

  /* ─── MASTER LOAD ───────────────────────────────────── */
  const loadAll = useCallback(async () => {
    if (!account || !contracts) return;
    setIsLoading(true);

    const ctl         = token.label === "YIN" ? contracts.yin : contracts.yang;
    const trackerAddr = await ctl.methods.dividendTracker().call();
    const distributor = buildContract(trackerAddr, distributorAbi);

    /* pull everything in parallel */
    const [
      totalRealRaw,
      divInfo,
      stakeInfo,
      totalDistRaw,
      holdersRaw,
      payoutsRaw
    ] = await Promise.all([
      distributor.methods.withdrawnDividendOf(account).call(),
      ctl.methods.getAccountDividendsInfo(account).call(),
      ctl.methods.getStakingInfo(account).call(),
      ctl.methods.getTotalDividendsDistributed().call(),
      ctl.methods.getNumberOfDividendTokenHolders().call(),
      ctl.methods.getPayoutTokens().call()                // <-- NEW
    ]);

    /* enrich payout array with logo / meta */
    let payoutTokens = Object.values(payoutsRaw);
    payoutTokens = payoutTokens.map(p => {
      const meta = tokenSelections.find(t =>
        t.address.toLowerCase() === p[0].toLowerCase()
      );
      return meta ? [p[0], p[1], meta] : p;
    });

    setClaimedDividends(Number(totalRealRaw));
    setUnclaimedRewards(Number(divInfo[3]));
    setStakingInfo({ until:Number(stakeInfo[0]), bonus:Number(stakeInfo[1]) });

    setContractData({
      totalDistributed : Number(totalDistRaw),
      holders          : Number(holdersRaw),
      payoutTokens
    });

    setIsLoading(false);
  }, [account, contracts, token, tokenSelections]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ─── live countdown (1-s interval) ─────────────────── */
  useEffect(() => {
    const id = setInterval(() => {
      setCountdown(
        stakingInfo.until > Date.now()/1000
          ? formatDistanceToNow(new Date(stakingInfo.until*1000),{addSuffix:true})
          : "Not Staked"
      );
    }, 1000);
    return () => clearInterval(id);
  }, [stakingInfo]);

  /* ─── actions ───────────────────────────────────────── */
  const handleClaim = async () => {
    setIsClaiming(true);
    try {
      const ctl = token.label === "YIN" ? contracts.yin : contracts.yang;
      await ctl.methods.claim().send({ from:account, gas:1_750_000 });
      await loadAll();
    } catch {}
    setIsClaiming(false);
  };

  const handleStake = async () => {
    if (!selectedDuration) return;
    setIsStaking(true);
    try {
      const ctl = token.label === "YIN" ? contracts.yin : contracts.yang;
      await ctl.methods.stake(selectedDuration).send({ from:account, gas:300_000 });
      setSelectedDuration(null);
      await loadAll();
    } catch {}
    setIsStaking(false);
  };

  /* ─── helpers ───────────────────────────────────────── */
  const WBNB      = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
  const WBNBprice = tokenSelections.find(t => t.address.toLowerCase() === WBNB.toLowerCase())?.price || 0;
  const toUSD     = amt => formatCurrencyExtended((amt/1e18)*WBNBprice);

  /* ─── render ────────────────────────────────────────── */
  return (
    <div style={{
      alignItems:"center", borderRadius:"10px",
      background:"rgba(50,100,255,0.05)",
      padding:"5px", fontSize:"11px"
    }}>
{ enableClaiming && <>
      {/* ── line 1: boost / unlock ─────────────────────── */}
      <div style={{ marginBottom:4, minHeight:"1rem" }}>
        Boost:&nbsp;
        <strong style={{ display:"inline-block", width:"30px" }}>
          {isLoading ? <Skeleton width="30px" height="1rem"/> : `${stakingInfo.bonus}%`}
        </strong>
        &nbsp;|&nbsp;Unstake:&nbsp;
        <strong style={{ display:"inline-block", width:"16ch" }}>
          {isLoading ? <Skeleton width="16ch" height="1rem"/> : countdown}
        </strong>
      </div>

      {/* ── stake controls (only if not locked) ─────────── */}
      {!isLoading && stakingInfo.until <= Date.now()/1000 && (
        <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:8 }}>
          <Dropdown
            value={selectedDuration}
            options={stakingOptions}
            onChange={e=>setSelectedDuration(e.value)}
            placeholder="Select Lock"
            optionLabel="label"
            style={{ width:"120px" }}
            disabled={isStaking}
          />
          <Button
            label="Stake" icon="pi pi-lock"
            size="small"
            disabled={isStaking || !selectedDuration}
            onClick={handleStake}
          />
        </div>
      )}
      </>}

      <hr/>

      {/* ── Rewards line (dual-token breakdown) ─────────── */}
      <div style={{ marginBottom:4 }}>
        Rewards:&nbsp;
        <strong>
          {contractData
            ? contractData.payoutTokens.map(p => `${p[1]}% ${p[2].label}`).join(", ")
            : <Skeleton width="120px" height="1rem"/>}
        </strong>
      </div>

      {/* ── Total distributed & holders ─────────────────── */}
      {viewTotalDistributed && (
        <div>
          Total Distributed:&nbsp;
          <strong>
            {contractData
              ? toUSD(contractData.totalDistributed)
              : <Skeleton width="100px" height="1rem"/>}
          </strong>
        </div>
      )}

      {contractData?.holders > 0 && (
        <div>Holder Count:&nbsp;
          <strong>
            {isLoading
              ? <Skeleton width="60px" height="1rem"/>
              : contractData.holders}
          </strong>
        </div>
      )}

      {/* ── earned / pending ───────────────────────────── */}
      {(claimedDividends === 0 && unclaimedRewards === 0) &&
        <div><strong>No rewards pending</strong></div>}

      {claimedDividends > 0 && (
        <div>Earned:&nbsp;<strong>{toUSD(claimedDividends)}</strong></div>
      )}

      {unclaimedRewards > 0 && (
        <div>Pending:&nbsp;<strong>{toUSD(unclaimedRewards)}</strong></div>
      )}

      {/* ── claim button ───────────────────────────────── */}
      {unclaimedRewards > 0 && enableClaiming && (
        isClaiming
          ? <Progress indeterminate style={{ marginTop:10 }}/>
          : <Button
              label="Claim"
              severity="warning"
              size="small"
              onClick={handleClaim}
              style={{ marginTop:10 }}
              disabled={isClaiming}
            />
      )}
    </div>
  );
});

export default YinYangRewards;

